import DragHandle from '@mui/icons-material/DragHandle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { ImageResizeAttributes } from './ImageResize';
import { positions, resizeHandler } from './resizeHandler';
import { useImagePopMenu } from './useImagePopMenu';

export interface ImageResizeNodeProps {
  updateAttributes: NodeViewProps['updateAttributes'];
  deleteNode: NodeViewProps['deleteNode'];
  node: {
    attrs: ImageResizeAttributes;
  };
}

export function ImageResizeNode({
  updateAttributes,
  deleteNode,
  node: { attrs },
}: ImageResizeNodeProps) {
  const { t } = useTranslation();
  const [resizeActive, setResizeActive] = useState(false);
  const { float, image, justify, ...imgAttrs } = attrs;
  const resizeBoxRef = useRef<HTMLDivElement>(null);
  const { ImagePopMenu, handlePopoverClose, handlePopoverOpen, open } =
    useImagePopMenu({ anchorRef: resizeBoxRef, updateAttributes, deleteNode });

  const handler = resizeHandler({
    resizeActive,
    resizeBoxRef,
    setResizeActive,
    updateAttributes,
  });

  return (
    <Box
      component={NodeViewWrapper}
      sx={[
        {
          position: 'relative',
          '&:hover': {
            '.drag-handle, .resize-box': {
              opacity: 1,
              transition: 'opacity 1s',
            },
          },
        },
        resizeActive
          ? {
              userSelect: 'none',
              MozUserSelect: 'none',
              WebkitUserSelect: 'none',
              msUserSelect: 'none',
              '.drag-handle, .resize-box': {
                opacity: 1,
              },
            }
          : {},
        justify ? { justifyContent: justify, display: 'flex' } : {},
      ]}
    >
      <Box
        sx={[
          {
            position: 'relative',
            display: 'inline-block',
            float: float ? float : 'none',
          },
        ]}
        className={`image-wrapper image-float-${float}`}
      >
        <Image
          alt={image.alt || ''}
          src={image.downloadUrls?.jpeg1000x1000 || image.originalUrl}
          width={imgAttrs.width || image.width || 100}
          height={imgAttrs.height || image.height || 100}
          style={{
            display: 'block',
            maxWidth: '100%',
            height: 'auto',
          }}
        />
        <Box
          className="resize-box"
          ref={resizeBoxRef}
          sx={{
            '--border-size': '2px',
            '--handle-size': '10px',
            position: 'absolute',
            border: 'var(--border-size) solid',
            borderColor: 'primary.main',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            opacity: 0,
            '.resize-handle': {
              position: 'absolute',
              width: 'var(--handle-size)',
              height: 'var(--handle-size)',
              backgroundColor: 'primary.main',
            },
          }}
          aria-owns={open ? 'mouse-over-popover' : undefined}
          aria-haspopup="true"
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
        >
          {positions.map((pos) => {
            let positionStyle: Record<string, string | number> = {};

            switch (pos) {
              case 'top-left':
                positionStyle = {
                  top: '0px',
                  left: '0px',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'nwse-resize',
                };
                break;
              case 'top-center':
                positionStyle = {
                  top: 0,
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'ns-resize',
                };
                break;
              case 'top-right':
                positionStyle = {
                  top: 0,
                  right: 0,
                  transform: 'translate(50%, -50%)',
                  cursor: 'nesw-resize',
                };
                break;
              case 'center-right':
                positionStyle = {
                  top: '50%',
                  right: 0,
                  transform: 'translate(50%, -50%)',
                  cursor: 'ew-resize',
                };
                break;
              case 'bottom-right':
                positionStyle = {
                  bottom: 0,
                  right: 0,
                  transform: 'translate(50%, 50%)',
                  cursor: 'nwse-resize',
                };
                break;
              case 'bottom-center':
                positionStyle = {
                  bottom: 0,
                  left: '50%',
                  transform: 'translate(-50%, 50%)',
                  cursor: 'ns-resize',
                };
                break;
              case 'bottom-left':
                positionStyle = {
                  bottom: 0,
                  left: 0,
                  transform: 'translate(-50%, 50%)',
                  cursor: 'nesw-resize',
                };
                break;
              case 'center-left':
                positionStyle = {
                  top: '50%',
                  left: 0,
                  transform: 'translate(-50%, -50%)',
                  cursor: 'ew-resize',
                };
                break;
            }
            return (
              <Box
                key={pos}
                className={`resize-handle ${pos}`}
                onMouseDown={(e) => handler(pos, e)}
                sx={positionStyle}
              />
            );
          })}
        </Box>
        <Button
          variant="contained"
          className="drag-handle"
          color="primary"
          aria-label={t('drag image') || 'drag image'}
          data-drag-handle
          sx={{
            cursor: 'move' /* fallback if grab cursor is unsupported */,
            opacity: 0,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DragHandle />
        </Button>
        <ImagePopMenu />
      </Box>
    </Box>
  );
}

export default ImageResizeNode;
